// Conciliación Legacy → Current (Sistema → LightData), de solo lectura:
// cada envío que entró a Legacy en el rango (por defecto, desde que se
// prendió el sincronizador), si está en Current y, si no, por qué y cómo se
// cierra. Lo arma GET /v2/integrations/lightdata/reconciliation; el CSV es el
// mismo pedido con format=csv.

import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { authFetch } from "../lib/authFetch";

export type Category = { code: string; missing: boolean; label: string; fix: string; count: number };
export type RecRow = {
  did: string; numero_tracking: string | null; origen: string | null; cliente: string | null; tienda: string | null;
  estado: string | null; first_seen_at: string | null; category: string; missing: boolean; decision: string | null;
  ld_delivery_id: number | null; ml_delivery_id: number | null; candidate_delivery_id: number | null; last_error: string | null;
};
export type Reconciliation = {
  range: { from: string; to: string; activation_since: string | null };
  total: number;
  missing: number;
  categories: Category[];
  by_channel: { channel: string; total: number; missing: number }[];
  by_store: { store: string; missing: number; channels: Record<string, number> }[];
  rows: RecRow[];
};

const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const sectionStyle: React.CSSProperties = { ...smallMutedStyle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "10px" };
const WARN = "var(--warning, #d29922)";
const thStyle: React.CSSProperties = { textAlign: "left", padding: "6px 8px", fontSize: "12px", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "6px 8px", fontSize: "13px", color: "var(--text-primary)", borderBottom: "1px solid var(--border)", verticalAlign: "top" };
const inputStyle: React.CSSProperties = { background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "10px", padding: "6px 8px", font: "inherit", fontSize: "13px" };

const fechaHora = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

// «2026-10-15T13:10» (datetime-local) → ISO; vacío → null.
export function aIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function consulta(desde: string, hasta: string, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  const f = aIso(desde); const t = aIso(hasta);
  if (f) p.set("from", f);
  if (t) p.set("to", t);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const q = p.toString();
  return `/v2/integrations/lightdata/reconciliation${q ? `?${q}` : ""}`;
}

export default function LightdataReconciliation({ token }: { token: string | null }) {
  const [data, setData] = useState<Reconciliation | null>(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pedido = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    setCargando(true);
    try {
      const res = await authFetch(consulta(desde, hasta), { token });
      const j = await res.json().catch(() => ({}));
      if (n !== pedido.current) return;
      if (!res.ok || j.ok === false) throw new Error(j.error || `Error ${res.status}`);
      setData(j); setError(null);
    } catch (e: any) {
      if (n === pedido.current) setError(e instanceof TypeError ? "Sin conexión con el servidor." : e?.message || "Error desconocido");
    } finally {
      if (n === pedido.current) setCargando(false);
    }
  }, [token, desde, hasta]);

  useEffect(() => { cargar(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const descargar = async () => {
    try {
      const res = await authFetch(consulta(desde, hasta, { format: "csv" }), { token });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `Error ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "conciliacion-lightdata.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { setError(`No se pudo descargar: ${e?.message || "error"}`); }
  };

  const faltantes = data ? data.rows.filter((r) => r.missing) : [];
  const etiqueta = (code: string) => data?.categories.find((c) => c.code === code)?.label ?? code;
  const enCurrent = data ? data.total - data.missing : 0;

  return (
    <Card>
      <div style={sectionStyle}>Conciliación Legacy → Current</div>
      <div style={{ ...smallMutedStyle, lineHeight: 1.5, marginBottom: "10px" }}>
        Cada envío que entró a Legacy en el rango, envío por envío: si está en Current y, si no, por qué. Los ya entregados o cancelados también cuentan: tienen que estar en Current. Solo lee, no cambia nada.
      </div>
      <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "12px" }}>
        <label style={smallMutedStyle}>Desde<br /><input aria-label="Desde" type="datetime-local" style={inputStyle} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label style={smallMutedStyle}>Hasta<br /><input aria-label="Hasta" type="datetime-local" style={inputStyle} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <Button variant="secondary" onClick={cargar} disabled={cargando}>{cargando ? "Consultando…" : "Consultar"}</Button>
        <Button variant="ghost" onClick={descargar} disabled={!data}>Descargar CSV</Button>
        <span style={smallMutedStyle}>Sin «Desde»: desde que se prendió el sincronizador.</span>
      </div>
      {error ? <div role="alert" style={{ color: "var(--danger)", fontSize: "13px", marginBottom: "8px" }}>{error}</div> : null}

      {data ? (
        <>
          <div style={{ fontSize: "14px", color: "var(--text-primary)", marginBottom: "12px" }}>
            Del {fechaHora(data.range.from)} al {fechaHora(data.range.to)}:{" "}
            <b>{enCurrent.toLocaleString("es-AR")} de {data.total.toLocaleString("es-AR")}</b> envíos están en Current,{" "}
            <b style={{ color: data.missing ? WARN : "var(--success)" }}>faltan {data.missing.toLocaleString("es-AR")}</b>.
          </div>

          <div style={{ overflowX: "auto", marginBottom: "16px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={thStyle}>Categoría</th><th style={{ ...thStyle, textAlign: "right" }}>Envíos</th><th style={thStyle}>Cómo se cierra</th></tr></thead>
              <tbody>
                {data.categories.filter((c) => c.count > 0).map((c) => (
                  <tr key={c.code}>
                    <td style={{ ...tdStyle, color: c.missing ? WARN : "var(--text-primary)" }}>{c.label}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{c.count.toLocaleString("es-AR")}</td>
                    <td style={tdStyle}>{c.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px", marginBottom: "16px" }}>
            <div>
              <div style={sectionStyle}>Por canal</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={thStyle}>Canal</th><th style={{ ...thStyle, textAlign: "right" }}>Total</th><th style={{ ...thStyle, textAlign: "right" }}>Faltan</th></tr></thead>
                <tbody>
                  {data.by_channel.map((c) => (
                    <tr key={c.channel}>
                      <td style={tdStyle}>{c.channel}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{c.total.toLocaleString("es-AR")}</td>
                      <td style={{ ...tdStyle, textAlign: "right", color: c.missing ? WARN : "var(--text-primary)" }}>{c.missing.toLocaleString("es-AR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div style={sectionStyle}>Faltantes por tienda</div>
              {data.by_store.length === 0 ? <div style={smallMutedStyle}>Ninguna.</div> : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={thStyle}>Tienda</th><th style={thStyle}>Canales</th><th style={{ ...thStyle, textAlign: "right" }}>Faltan</th></tr></thead>
                  <tbody>
                    {data.by_store.map((s) => (
                      <tr key={s.store}>
                        <td style={tdStyle}>{s.store}</td>
                        <td style={tdStyle}>{Object.entries(s.channels).map(([k, v]) => `${k} ${v}`).join(" · ")}</td>
                        <td style={{ ...tdStyle, textAlign: "right", color: WARN }}>{s.missing.toLocaleString("es-AR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div style={sectionStyle}>Detalle de los faltantes · {faltantes.length.toLocaleString("es-AR")}</div>
          {faltantes.length === 0 ? <div style={smallMutedStyle}>No falta nada en el rango.</div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={thStyle}>LightData</th><th style={thStyle}>Tracking</th><th style={thStyle}>Canal</th>
                  <th style={thStyle}>Cliente → tienda</th><th style={thStyle}>Estado</th><th style={thStyle}>Visto</th><th style={thStyle}>Categoría</th>
                </tr></thead>
                <tbody>
                  {faltantes.map((r) => (
                    <tr key={r.did}>
                      <td style={{ ...tdStyle, fontFamily: "monospace" }}>{r.did}</td>
                      <td style={{ ...tdStyle, fontFamily: "monospace" }}>{r.numero_tracking || "—"}</td>
                      <td style={tdStyle}>{r.origen || "—"}</td>
                      <td style={tdStyle}>{r.cliente || "(sin nombre)"} → {r.tienda || <span style={{ color: WARN }}>sin tienda</span>}</td>
                      <td style={tdStyle}>{r.estado || "—"}</td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{fechaHora(r.first_seen_at)}</td>
                      <td style={tdStyle}>{etiqueta(r.category)}{r.last_error ? <div style={smallMutedStyle}>{r.last_error}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : cargando ? <div style={smallMutedStyle}>Cargando…</div> : null}
    </Card>
  );
}
